import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Car, Plus, Trash2, ArrowLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    make: "",
    model: "",
    license_plate: "",
    color: "",
    vehicle_type: "",
    capacity: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      toast.error("Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/vehicles`, newVehicle);
      toast.success("Vehicle added successfully!");
      setShowAddDialog(false);
      setNewVehicle({ make: "", model: "", license_plate: "", color: "" });
      fetchVehicles();
    } catch (error) {
      toast.error("Failed to add vehicle");
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to delete this vehicle?")) return;

    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`);
      toast.success("Vehicle deleted");
      fetchVehicles();
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="vehicles-page">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>

        <div className="fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">My Vehicles</h1>
              <p className="text-xl text-gray-600">Manage your registered vehicles.</p>
            </div>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-vehicle-btn"
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-6 py-6"
                >
                  <Plus className="mr-2" size={20} />
                  Add Vehicle
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-[#1A3C34]">Add New Vehicle</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddVehicle} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="vehicle_type" className="text-gray-700 font-medium">
                      Vehicle Type
                    </Label>
                    <Select
                      id="vehicle_type"
                      data-testid="vehicle-type"
                      placeholder="Car, Bike, Electric Car, Electric Bike"
                      value={newVehicle.vehicle_type}
                      onValueChange={(value) => setNewVehicle({ ...newVehicle, vehicle_type: value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    >
                      <SelectTrigger className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12 text-base">
                        <SelectValue placeholder="Select vehicle type" />
                        </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Car">Car</SelectItem>
                        <SelectItem value="Bike">Bike</SelectItem>
                        <SelectItem value="electric_car">Electric Car</SelectItem>
                        <SelectItem value="electric_bike">Electric Bike</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="make" className="text-gray-700 font-medium">
                      Make
                    </Label>
                    <Input
                      id="make"
                      data-testid="vehicle-make"
                      placeholder="Toyota"
                      value={newVehicle.make}
                      onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="model" className="text-gray-700 font-medium">
                      Model
                    </Label>
                    <Input
                      id="model"
                      data-testid="vehicle-model"
                      placeholder="Camry"
                      value={newVehicle.model}
                      onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="license" className="text-gray-700 font-medium">
                      License Plate
                    </Label>
                    <Input
                      id="license"
                      data-testid="vehicle-license"
                      placeholder="ABC-1234"
                      value={newVehicle.license_plate}
                      onChange={(e) =>
                        setNewVehicle({ ...newVehicle, license_plate: e.target.value })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="color" className="text-gray-700 font-medium">
                      Color
                    </Label>
                    <Input
                      id="color"
                      data-testid="vehicle-color"
                      placeholder="Silver"
                      value={newVehicle.color}
                      onChange={(e) => setNewVehicle({ ...newVehicle, color: e.target.value })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="capacity" className="text-gray-700 font-medium">
                      Capacity
                    </Label>
                    <Input
                      id="capacity"
                      data-testid="vehicle-capacity"
                      placeholder="4"
                      value={
                        newVehicle.vehicle_type === "Bike" || newVehicle.vehicle_type === "electric_bike"
                          ? 1
                          :
                        newVehicle.capacity}
                      onChange={(e) => setNewVehicle({ ...newVehicle, capacity: 
                        newVehicle.vehicle_type === "Bike" || newVehicle.vehicle_type === "electric_bike" ? 1 :
                        parseInt(e.target.value) || 0 })}
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                      disabled={newVehicle.vehicle_type === "Bike" || newVehicle.vehicle_type === "electric_bike"}
                    />
                  </div>
                  <Button
                    data-testid="submit-vehicle"
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                  >
                    Add Vehicle
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {vehicles.length === 0 ? (
            <Card className="p-12 text-center">
              <Car className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg mb-4">No vehicles added yet</p>
              <Button
                data-testid="add-first-vehicle"
                onClick={() => setShowAddDialog(true)}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
              >
                <Plus className="mr-2" size={20} />
                Add Your First Vehicle
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                <Card
                  key={vehicle._id}
                  data-testid={`vehicle-${vehicle._id}`}
                  className="p-6 border border-gray-100 hover:shadow-xl transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-[#1A3C34] rounded-2xl flex items-center justify-center">
                      <Car className="text-white" size={28} />
                    </div>
                    <Button
                      data-testid={`delete-vehicle-${vehicle._id}`}
                      onClick={() => handleDeleteVehicle(vehicle._id)}
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 rounded-full p-2"
                    >
                      <Trash2 size={20} />
                    </Button>
                  </div>

                  <h3 className="text-2xl font-bold text-[#1A3C34] mb-2">
                    {vehicle.make} {vehicle.model}
                  </h3>

                  <div className="space-y-2">
                    <p className="text-gray-600">
                      <span className="font-medium">License:</span> {vehicle.license_plate}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Color:</span> {vehicle.color}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehiclesPage;